import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, CheckCircle2, XCircle, Search } from "lucide-react";
import client, { extractError } from "@/api";

const STATUS_LABEL = {
  PENDING_REVIEW: "Pending review",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  CANCELLED: "Cancelled",
};

const OPTIONS = [
  { value: "PENDING_REVIEW", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "", label: "All" },
];

function RejectDialog({ onCancel, onConfirm, busy }) {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  return (
    <div className="dialog-backdrop" role="dialog" data-testid="trade-reject-dialog">
      <form className="dialog-panel" onSubmit={(e) => { e.preventDefault(); onConfirm(reason, notes); }}>
        <h3>Reject trade request</h3>
        <label>Reason (visible to buyer, minimum 5 characters)
          <textarea required minLength={5} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="trade-reject-reason" />
        </label>
        <label>Internal admin notes (optional)
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="trade-reject-notes" />
        </label>
        <div className="dialog-actions">
          <button type="button" className="ghost-button" onClick={onCancel} data-testid="trade-reject-cancel">Cancel</button>
          <button type="submit" className="primary-button" disabled={busy} data-testid="trade-reject-confirm">{busy ? "Working…" : "Confirm rejection"}</button>
        </div>
      </form>
    </div>
  );
}

export default function AdminTradeRequestsPage() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("PENDING_REVIEW");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const load = async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (filter) params.set("status", filter);
      if (search) params.set("search", search);
      params.set("page_size", 50);
      const r = await client.get(`/admin/trade-requests?${params.toString()}`);
      setItems(r.data.items || []); setCounts(r.data.counts || {});
    } catch (err) { setError(extractError(err, "Unable to load trade requests.")); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const openDetail = async (id) => {
    try { const r = await client.get(`/admin/trade-requests/${id}`); setDetail(r.data); setMessage(""); setError(""); }
    catch (err) { setError(extractError(err)); }
  };

  const approve = async () => {
    if (!detail) return;
    setBusy(true); setError("");
    try {
      const r = await client.post(`/admin/trade-requests/${detail.id}/approve`, { admin_notes: null });
      setDetail((d) => ({ ...d, ...r.data }));
      const txnId = r.data.transaction_id;
      setMessage(
        txnId
          ? `Trade request approved. Transaction ${txnId.slice(0, 8)} created and ready for fulfilment.`
          : "Trade request approved."
      );
      load();
    } catch (err) { setError(extractError(err)); }
    finally { setBusy(false); }
  };

  const reject = async (reason, notes) => {
    if (!detail) return;
    setBusy(true); setError("");
    try {
      const r = await client.post(`/admin/trade-requests/${detail.id}/reject`, { reason, admin_notes: notes || null });
      setDetail((d) => ({ ...d, ...r.data }));
      setDialog(null); setMessage("Trade request rejected.");
      load();
    } catch (err) { setError(extractError(err)); }
    finally { setBusy(false); }
  };

  if (detail) {
    return (
      <div className="admin-company-detail" data-testid="admin-trade-request-detail">
        <button type="button" className="link-button back" data-testid="admin-trade-back" onClick={() => setDetail(null)}><ChevronLeft size={14} /> Back to trade requests</button>
        <header className="detail-header">
          <div>
            <p className="eyebrow">{STATUS_LABEL[detail.status]}</p>
            <h2>{detail.listing_title}</h2>
            <p className="muted">Buyer: {detail.buyer_name} ({detail.buyer_status}) · Seller: {detail.seller_name} ({detail.seller_status})</p>
          </div>
          <div className="chip-stack">
            <span className={`status-chip status-${detail.status.toLowerCase()}`}>{STATUS_LABEL[detail.status]}</span>
          </div>
        </header>
        {error && <div className="form-error">{error}</div>}
        {message && <div className="form-success" data-testid="admin-trade-message">{message}</div>}
        <section className="detail-grid">
          <article><p className="eyebrow">TRADE VALUE</p><p><strong>₹{detail.trade_value.toLocaleString("en-IN")}</strong></p></article>
          <article><p className="eyebrow">FACILITATOR FEE ({detail.facilitator_fee_rate_percent}%)</p><p>₹{detail.facilitator_fee_amount.toLocaleString("en-IN")}</p></article>
          <article><p className="eyebrow">TOTAL CREDIT NEED</p><p><strong>₹{detail.total_credit_requirement.toLocaleString("en-IN")}</strong></p></article>
          <article><p className="eyebrow">QUANTITY</p><p>{detail.requested_quantity ?? "—"} {detail.quantity_unit || ""}</p></article>
          <article><p className="eyebrow">UNIT VALUE</p><p>₹{detail.unit_value.toLocaleString("en-IN")}</p></article>
          <article><p className="eyebrow">CREATED</p><p>{new Date(detail.created_at).toLocaleString()}</p></article>
          {detail.buyer_note && <article className="wide"><p className="eyebrow">BUYER NOTE</p><p>{detail.buyer_note}</p></article>}
          {detail.admin_review_notes && <article className="wide"><p className="eyebrow">INTERNAL ADMIN NOTES</p><p>{detail.admin_review_notes}</p></article>}
          {detail.rejection_reason && <article className="wide"><p className="eyebrow">REJECTION REASON</p><p>{detail.rejection_reason}</p></article>}
        </section>
        {detail.status === "PENDING_REVIEW" && (
          <section className="section-block" data-testid="admin-trade-actions">
            <div className="button-row">
              <button type="button" className="primary-button" onClick={approve} disabled={busy} data-testid="admin-trade-approve"><CheckCircle2 size={14} /> Approve</button>
              <button type="button" className="danger-button-solid" onClick={() => setDialog("reject")} disabled={busy} data-testid="admin-trade-reject"><XCircle size={14} /> Reject</button>
            </div>
          </section>
        )}
        {detail.audit_log?.length > 0 && (
          <section className="documents-section">
            <div className="section-heading"><div><p className="eyebrow">HISTORY</p><h3>Audit trail</h3></div></div>
            <ul className="audit-list" data-testid="admin-trade-audit">
              {detail.audit_log.map((a) => (
                <li key={a.id}>
                  <strong>{a.event}</strong><small>{a.previous_state || "—"} → {a.new_state || "—"}</small>
                  {a.reason && <p className="muted small">{a.reason}</p>}
                  {a.admin_notes && <p className="muted small">Notes: {a.admin_notes}</p>}
                  <small className="muted">{a.actor_email || "system"} · {a.actor_role} · {new Date(a.created_at).toLocaleString()}</small>
                </li>
              ))}
            </ul>
          </section>
        )}
        {dialog === "reject" && <RejectDialog onCancel={() => setDialog(null)} onConfirm={reject} busy={busy} />}
      </div>
    );
  }

  return (
    <div className="admin-listings-page" data-testid="admin-trade-requests-page">
      <div className="admin-toolbar">
        <div className="filter-tabs" data-testid="admin-trade-tabs">
          {OPTIONS.map((o) => (
            <button key={o.value || "all"} type="button" data-testid={`admin-trade-filter-${o.value || "all"}`} className={filter === o.value ? "active" : ""} onClick={() => setFilter(o.value)}>
              {o.label}{o.value && counts[o.value] > 0 && <span className="count">{counts[o.value]}</span>}
            </button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="search-row">
          <input placeholder="Search listing title" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-trade-search" />
          <button type="submit" className="ghost-button" data-testid="admin-trade-search-btn"><Search size={12} /></button>
        </form>
      </div>
      {error && <div className="form-error">{error}</div>}
      {loading ? <div className="loading-block">Loading…</div> : (
        <table className="admin-table" data-testid="admin-trade-requests-table">
          <thead><tr><th>Listing</th><th>Buyer</th><th>Seller</th><th>Trade value</th><th>Fee</th><th>Total</th><th>Status</th><th>Created</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={9} className="empty-row">No trade requests in this view.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} data-testid={`admin-trade-row-${r.id}`}>
                <td><strong>{r.listing_title}</strong><br /><small className="muted">{r.listing_type}</small></td>
                <td>{r.buyer_name}<br /><small className="muted">{r.buyer_status}</small></td>
                <td>{r.seller_name}<br /><small className="muted">{r.seller_status}</small></td>
                <td>₹{r.trade_value.toLocaleString("en-IN")}</td>
                <td>₹{r.facilitator_fee_amount.toLocaleString("en-IN")}</td>
                <td><strong>₹{r.total_credit_requirement.toLocaleString("en-IN")}</strong></td>
                <td><span className={`status-chip status-${r.status.toLowerCase()}`}>{STATUS_LABEL[r.status]}</span></td>
                <td><small>{new Date(r.created_at).toLocaleDateString()}</small></td>
                <td><button type="button" className="link-button" data-testid={`admin-open-trade-${r.id}`} onClick={() => openDetail(r.id)}>Review →</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
